import React, { useState, useRef, useEffect } from "react";
import "./LivenessCapture.css";
import { detectFaceInCircle, drawFaceCircle } from "../../utils/livenessHelper";

function LivenessCapture({ onBack, onContinue, isSubmitting = false }) {
  const [captureMode, setCaptureMode] = useState("guide"); // guide, recording, review
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState(
    "Position your face in the circle"
  );

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingMimeTypeRef = useRef("video/webm");
  const streamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const detectionIntervalRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const isRecordingRef = useRef(false);
  const isDiscardRef = useRef(false);

  useEffect(() => {
    if (captureMode === "recording" || captureMode === "guide") {
      // Small delay to ensure cleanup is complete
      const timer = setTimeout(() => {
        startCamera();
      }, 100);
      return () => {
        clearTimeout(timer);
        stopCamera();
      };
    } else {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: 640,
          height: 480,
          frameRate: { ideal: 24, max: 24 }, // target ~24 FPS for consistent frame extraction
        },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        videoRef.current.onloadeddata = () => {
          startFaceDetection();
        };
      }
    } catch (error) {
      console.error("Camera access error:", error);
      alert("Unable to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    // Clear all intervals and timers
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    // Stop media recorder
    if (mediaRecorderRef.current) {
      if (
        mediaRecorderRef.current.state === "recording" ||
        mediaRecorderRef.current.state === "paused"
      ) {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }

    // Stop all camera tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Clear video source
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const cancelRecording = (message) => {
    isDiscardRef.current = true;
    setIsRecording(false);
    isRecordingRef.current = false;
    setRecordingProgress(0);
    setStatusMessage(message || "Position your face in the circle");

    // Stop MediaRecorder if active
    if (
      mediaRecorderRef.current &&
      (mediaRecorderRef.current.state === "recording" ||
        mediaRecorderRef.current.state === "paused")
    ) {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.error("Error stopping recorder on cancel:", err);
      }
    }

    // Clear timers
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startFaceDetection = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    detectionIntervalRef.current = setInterval(() => {
      if (!video || video.readyState !== 4) return;

      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const detection = detectFaceInCircle(
        imageData,
        canvas.width,
        canvas.height
      );

      setFaceDetected(detection.faceDetected);

      if (isRecordingRef.current) {
        if (!detection.faceDetected) {
          cancelRecording("Face lost! Keep your face inside the frame.");
          return;
        }
        setStatusMessage("Recording... Please blink naturally");
      } else {
        setStatusMessage(
          detection.faceDetected
            ? "✓ Face detected - Ready to record"
            : "Position your face inside the frame"
        );
      }

      drawFaceCircle(ctx, detection);
    }, 100);
  };

  const startRecording = () => {
    if (!streamRef.current || isRecording) return;

    setIsRecording(true);
    isRecordingRef.current = true;
    setRecordingProgress(0);
    setStatusMessage("Recording... Please blink naturally");
    recordedChunksRef.current = [];

    // Prefer MP4 (h264) if supported; fallback to WebM VP8
    const preferredTypes = ["video/mp4;codecs=avc1.42E01E", "video/mp4"];

    let selectedType = "";
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
      for (const t of preferredTypes) {
        if (MediaRecorder.isTypeSupported(t)) {
          selectedType = t;
          break;
        }
      }
    }

    const mediaRecorder = selectedType
      ? new MediaRecorder(streamRef.current, { mimeType: selectedType })
      : new MediaRecorder(streamRef.current);
    recordingMimeTypeRef.current =
      selectedType || mediaRecorder.mimeType || "video/webm";

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunksRef.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      if (isDiscardRef.current) {
        isDiscardRef.current = false;
        recordedChunksRef.current = [];
        return;
      }

      const blob = new Blob(recordedChunksRef.current, {
        type: recordingMimeTypeRef.current || "video/webm",
      });

      const fileExtension = blob.type.includes("mp4") ? "mp4" : "webm";

      const file = new File([blob], `liveness_${Date.now()}.${fileExtension}`, {
        type: blob.type,
        lastModified: Date.now(),
      });

      setRecordedVideo(file);
      setCaptureMode("review");
      stopCamera();
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;

    const startTime = Date.now();
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / 5000) * 100, 100);
      setRecordingProgress(progress);
      if (progress >= 100) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }, 50);

    recordingTimerRef.current = setTimeout(() => {
      setIsRecording(false);
      isRecordingRef.current = false;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop();
      }
    }, 5000);
  };

  const handleRetake = () => {
    // Reset all states
    setRecordedVideo(null);
    setFaceDetected(false);
    setIsRecording(false);
    isRecordingRef.current = false;
    setRecordingProgress(0);
    setStatusMessage("Position your face in the circle");
    recordedChunksRef.current = [];

    // Clear any ongoing timers/intervals
    if (detectionIntervalRef.current)
      clearInterval(detectionIntervalRef.current);
    if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    // Stop media recorder if active
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    // Go back to recording mode (camera will restart via useEffect)
    setCaptureMode("recording");
  };

  const handleConfirm = () => {
    onContinue(recordedVideo);
  };

  useEffect(() => {
    if (captureMode === "recording" && faceDetected && !isRecording) {
      startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode, faceDetected, isRecording]);
  if (captureMode === "guide") {
    return (
      <div className="capture-container">
        <div className="capture-card">
          <div className="capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "90%" }}
              ></div>
            </div>
          </div>

          <h2 className="capture-title">Liveness Verification</h2>
          <p className="capture-subtitle">
            We need to verify you're a real person
          </p>

          <div className="guide-section">
            <div className="liveness-illustration">
              <div className="face-circle-guide">
                {/* <div className="face-icon">
                                    <div className="eye left"></div>
                                    <div className="eye right"></div>
                                    <div className="mouth"></div>
                                </div> */}
                <svg
                  className="face-icon-svg"
                  viewBox="0 0 100 100"
                  fill="none"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="white"
                    strokeWidth="3"
                    opacity="0.3"
                  />
                  <circle cx="35" cy="40" r="5" fill="white" />
                  <circle cx="65" cy="40" r="5" fill="white" />
                  <path
                    d="M 30 65 Q 50 75 70 65"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </div>
            </div>

            <div className="guidelines-list">
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Position your face in the circle</span>
              </div>
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Blink your eyes naturally during recording</span>
              </div>
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Recording will be 5 seconds long</span>
              </div>
              <div className="guideline-item">
                <svg
                  className="guideline-icon check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline
                    points="20 6 9 17 4 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Ensure good lighting on your face</span>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="action-btn capture-btn"
              onClick={() => setCaptureMode("recording")}
            >
              <svg
                className="btn-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
              </svg>
              Start Recording
            </button>
          </div>

          <div className="capture-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  // Recording Screen
  if (captureMode === "recording") {
    return (
      <div className="capture-container">
        <div className="capture-card camera-mode">
          <div className="camera-view">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-video"
              style={{ transform: "scaleX(-1)" }}
            ></video>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

            <div className="liveness-camera-overlay">
              <div
                className={`liveness-face-outline ${faceDetected ? "detected" : ""
                  } ${isRecording ? "recording" : ""}`}
              ></div>
              {isRecording && (
                <div className="recording-progress">
                  <div className="recording-dot"></div>
                  <span>REC</span>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${recordingProgress}%` }}
                    ></div>
                  </div>
                  <span>{Math.ceil((100 - recordingProgress) / 20)}s</span>
                </div>
              )}
            </div>

            <div className="liveness-overlay">
              <div className="liveness-status">
                <div
                  className={`status-indicator ${faceDetected ? "active" : ""}`}
                >
                  {faceDetected ? "✓" : "○"} Face
                </div>
              </div>

              <div className="camera-hint">{statusMessage}</div>
            </div>

            <div className="camera-controls">
              <button
                className="control-btn cancel"
                onClick={() => {
                  stopCamera();
                  setCaptureMode("guide");
                }}
                disabled={isRecording}
              >
                Cancel
              </button>
              <button
                className="control-btn capture"
                onClick={startRecording}
                disabled={isRecording}
              >
                <div className="capture-circle">
                  {isRecording && <div className="recording-pulse"></div>}
                </div>
              </button>
              <div className="control-spacer"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review Screen
  if (captureMode === "review") {
    return (
      <div className="capture-container">
        <div className="capture-card">
          <div className="capture-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "90%" }}
              ></div>
            </div>
          </div>

          <h2 className="capture-title">Review your video</h2>
          <p className="capture-subtitle">
            Make sure your face is clearly visible
          </p>

          <div className="review-section">
            <p className="review-label">RECORDED VIDEO</p>
            <div className="video-preview">
              {recordedVideo && (
                <video
                  src={URL.createObjectURL(recordedVideo)}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="preview-video"
                  style={{ transform: "scaleX(-1)" }}
                />
              )}
            </div>
          </div>

          <div className="review-buttons">
            <button
              className="review-btn retake"
              onClick={handleRetake}
              disabled={isSubmitting}
            >
              Retake video
            </button>
            <button
              className="review-btn confirm"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner"></span>
                  Submitting...
                </>
              ) : (
                "Looks good"
              )}
            </button>
          </div>

          <div className="capture-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  return null;
}

export default LivenessCapture;
